// [claude-code 2026-05-05] Tight-shell PsychAssist: reduced header width and compact monitor presentation (pulse + waveform + score only).
// [claude-code 2026-04-17] Migrated drag to useDraggable hook (pointer events + rAF); grip-only; dock-on-release via onDragEnd
// [claude-code 2026-04-20] S21: Voice activation button + live waveform indicator
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ArrowUp, GripVertical, PictureInPicture2, X } from "lucide-react";
import { CompactERMonitor } from "../mission-control/CompactERMonitor";
import { useDraggable } from "../../hooks/useDraggable";
import {
  resolvePsychAssistEmphasis,
  type PsychAssistEmphasis,
} from "./psych-assist-emphasis";
// [Codex 2026-07-04] Voice indicators are owned by the Voice CAO dock; this
// PsychAssist shell remains a status badge only.

export type PsychAssistDockTarget = "floating" | "header";

interface PsychAssistDockableProps {
  target: PsychAssistDockTarget;
  onDockToHeader: () => void;
  onUndockToFloating: () => void;
  onClose?: () => void;
  storageKey?: string;
  headerDockZoneId?: string;
}

export function PsychAssistDockable({
  target,
  onDockToHeader,
  onUndockToFloating,
  onClose,
  storageKey = "fintheon:psychassist-floating-pos:v1",
  headerDockZoneId = "fintheon-heading-toolbar",
}: PsychAssistDockableProps) {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [emphasis, setEmphasis] = useState<PsychAssistEmphasis>("none");
  const panelRef = useRef<HTMLDivElement>(null);
  const gripRef = useRef<HTMLButtonElement>(null);
  const previousScoreRef = useRef<number | null>(null);
  const emphasisTimerRef = useRef<number | null>(null);

  const floating = target === "floating";

  const defaultPos = useMemo(() => {
    if (typeof window === "undefined") return { x: 24, y: 86 };
    return { x: Math.max(24, window.innerWidth - 360), y: 86 };
  }, []);

  const handleDragEnd = useCallback(
    (finalPos: { x: number; y: number }) => {
      const dockZone = document.getElementById(headerDockZoneId);
      if (!dockZone) return;
      const rect = dockZone.getBoundingClientRect();
      // Use the centre-ish of the grip (finalPos + offset)
      const probeX = finalPos.x + 14;
      const probeY = finalPos.y + 14;
      const inside =
        probeX >= rect.left &&
        probeX <= rect.right &&
        probeY >= rect.top &&
        probeY <= rect.bottom;
      if (inside) onDockToHeader();
    },
    [headerDockZoneId, onDockToHeader],
  );

  useDraggable({
    elementRef: panelRef,
    handleRef: gripRef,
    storageKey,
    bounds: "viewport",
    initialPosition: defaultPos,
    onDragEnd: handleDragEnd,
    disabled: !floating,
  });

  useEffect(() => {
    if (target === "header") {
      const raf = requestAnimationFrame(() => setHeaderVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setHeaderVisible(false);
  }, [target]);

  useEffect(() => {
    return () => {
      if (emphasisTimerRef.current !== null) {
        window.clearTimeout(emphasisTimerRef.current);
      }
    };
  }, []);

  const handleScoreChange = useCallback((score: number) => {
    const next = resolvePsychAssistEmphasis(previousScoreRef.current, score);
    previousScoreRef.current = score;
    if (next === "none") return;
    setEmphasis(next);
    if (emphasisTimerRef.current !== null) {
      window.clearTimeout(emphasisTimerRef.current);
    }
    emphasisTimerRef.current = window.setTimeout(() => {
      setEmphasis("none");
      emphasisTimerRef.current = null;
    }, 1600);
  }, []);

  const handleUndock = useCallback(() => {
    setHeaderVisible(false);
    const timer = setTimeout(() => onUndockToFloating(), 280);
    return () => clearTimeout(timer);
  }, [onUndockToFloating]);

  const body = useMemo(() => {
    const compactMonitor = target === "header";
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <CompactERMonitor
            compact={compactMonitor}
            onERScoreChange={handleScoreChange}
          />
        </div>
      </div>
    );
  }, [handleScoreChange, target]);

  const emphasisClass = emphasisToneClass(emphasis);

  if (!floating) {
    return (
      <div
        className={`flex h-7 items-center gap-2 overflow-hidden rounded-lg bg-[var(--fintheon-bg)] px-3.5 ring-1 ring-transparent transition-[box-shadow] duration-200 ${emphasisClass}`}
        data-psychassist-emphasis={emphasis}
        style={{
          width: headerVisible ? 228 : 0,
          opacity: headerVisible ? 1 : 0,
          transition:
            "width 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease",
        }}
      >
        <button
          onClick={handleUndock}
          className="rounded p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-primary)]"
          title="Picture-in-picture (float)"
          aria-label="Float PsychAssist"
        >
          <PictureInPicture2 className="w-3.5 h-3.5" />
        </button>
        <div className="min-w-[150px] max-w-[188px]">{body}</div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-primary)]"
            title="Hide"
            aria-label="Hide PsychAssist"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`fixed z-50 rounded-2xl bg-[var(--fintheon-surface)] px-3 py-2 ring-1 ring-transparent transition-[box-shadow,ring-color] duration-200 ${emphasisClass}`}
      data-psychassist-emphasis={emphasis}
      style={{
        top: 0,
        left: 0,
        width: "340px",
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <button
            ref={gripRef}
            className="cursor-grab touch-none rounded p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-primary)] active:cursor-grabbing"
            title="Drag"
            aria-label="Drag PsychAssist"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <button
            onClick={onDockToHeader}
            className="rounded p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-primary)]"
            title="Dock PsychAssist in header"
            aria-label="Dock PsychAssist in header"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <span className="text-[10px] text-[var(--fintheon-accent)]/70 tracking-[0.18em] uppercase">
            PsychAssist
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-primary)]"
            title="Hide"
            aria-label="Hide PsychAssist"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div>{body}</div>
      <div className="mt-2 text-[10px] text-[var(--fintheon-muted)]/70">
        Drag to the header or use the dock control.
      </div>
    </div>
  );
}

function emphasisToneClass(emphasis: PsychAssistEmphasis): string {
  if (emphasis === "improving") {
    return "ring-[var(--fintheon-bullish)]/45";
  }
  if (emphasis === "declining") {
    return "ring-[var(--fintheon-bearish)]/50";
  }
  return "";
}
